import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';
import { isFieldVisible as checkVisible, type VisibilityConfig } from '../config/fieldVisibilityConfig';

/**
 * Hook that provides an `isVisible(fieldKey)` function based on the
 * admin-configured field-visibility matrix and the current contract type.
 * Fails open: if the setting can't be loaded, all fields are visible.
 */
export function useFieldVisibility(contractType: string) {
  const [config, setConfig] = useState<VisibilityConfig>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    adminApi.getPublicSetting('field_visibility')
      .then(res => {
        if (cancelled) return;
        try {
          const parsed = JSON.parse(res.data.value || '{}');
          setConfig(parsed);
        } catch {
          setConfig({});
        }
      })
      .catch(() => { if (!cancelled) setConfig({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const isVisible = useCallback(
    (fieldKey: string) => checkVisible(config, contractType, fieldKey),
    [config, contractType]
  );

  return { isVisible, loading };
}
